import { computed } from 'mobx';
import { realDealFeatures as Features } from '@wix/premium-marketing-data/dist/src/marketing-data/real-deal/features';
import {
  Country,
  Experiments,
  PaymentCycle,
  PlanType,
  ProductFamily,
  Template,
} from 'enums';
import {
  AdvancedPlansFeature,
  AdvancedPlansFeatureSection,
  AdvancedPlansProductData,
  CycleDetails,
  FeaturesGroupSection,
  ProductData,
} from 'types';
import { getSaleDiscountPercentage, isCycleOfMonths } from 'utils';
import { CyclesProps } from './Cycles';
import { WithTranslation } from 'react-i18next';

export class CyclesController {
  constructor(private readonly props: CyclesProps) { }

  isBordred = (index: number): boolean =>
    index >= 1 &&
    index < this.props.rootStore.appStore.currentProduct.cycles.length;

  isCycleSelected = (cycleId: PaymentCycle) =>
    cycleId === this.props.rootStore.appStore.currentCycle;

  isAscendBundle({ currentProduct, currentCycle }): boolean {
    return (
      [PaymentCycle.YEAR2, PaymentCycle.YEAR3].includes(currentCycle) &&
      currentProduct.isAscendBundle
    );
  }
  isMonthlyDisabledInProduct = (feature: AdvancedPlansFeature) => {
    const { currentProduct } = this.props.rootStore.appStore;
    return (
      feature.includedInProducts.has(currentProduct.id.toString()) &&
      feature.disabledInMonthlyCycle
    );
  };

  missingFreeCouponsFeatures = () => {
    const { experiments } = this.props.rootStore.appStore.appContext;
    if (
      !(experiments[Experiments.EnableImmediateCouponsDeliverySpec] === 'true')
    ) {
      return [];
    }

    return [
      {
        translationKey:
          'premium-purchase-plan.cycle_selection.monthly_warning.social_media_marketing',
      },
      {
        translationKey:
          'premium-purchase-plan.cycle_selection.monthly_warning.email_marketing_plan',
      },
    ] as AdvancedPlansFeature[];
  };

  missingWixelLightCouponFeatures = () => {
    const { shouldShowWixelLightCoupon, currentProduct: { productFamilyId } } = this.props.rootStore.appStore;
    if (!shouldShowWixelLightCoupon(productFamilyId ?? '')) {
      return [];
    }
    return [
      { translationKey: 'premium-purchase-plan.cycle_slection.monthly_warning_box.no_free_plan' },
    ] as AdvancedPlansFeature[];
  };

  missingMonthlyInSections = (
    sectionsOrGroups: (AdvancedPlansFeatureSection | FeaturesGroupSection)[],
  ) => {
    return sectionsOrGroups
      ?.map(({ features: sectionFeatures }) =>
        sectionFeatures.filter(this.isMonthlyDisabledInProduct),
      )
      .reduce((acc, next) => [...acc, ...next], []);
  };

  getGridRowsConfig = () => {
    const {
      pageMode: { template },
      country,
    } = this.props.rootStore.appStore.appContext;
    const rowHeight =
      country === Country.BR
        ? '78px'
        : template === Template.CandyBar
          ? '66px'
          : '72px';
    return {
      '--grid-row-height': rowHeight,
      '--grid-rows-count': this.cycles.length,
    } as React.CSSProperties;
  };

  @computed
  get showDisclaimer() {
    const { currentProduct, currentCycle } = this.props.rootStore.appStore;
    return (
      currentProduct.productFamily !== ProductFamily.Enterprise &&
      currentProduct.productFamily !== ProductFamily.ConnectDomain &&
      isCycleOfMonths(currentCycle) &&
      !!this.missingFeatureList.length
    );
  }
  get isBusinessProduct() {
    const { currentProduct } = this.props.rootStore.appStore;
    return currentProduct.type === PlanType.Business;
  }

  @computed
  get missingFeatureList() {
    const {
      appContext: {
        features: websiteFeatures,
        businessFeatures,
        sections,
        websiteFeatureGroups,
        businessFeatureGroups,
        featureGroups,
        experiments,
      },
      isSunriseV2Enabled,
    } = this.props.rootStore.appStore;

    const features = this.isBusinessProduct
      ? businessFeatures
      : websiteFeatures;

    const groups = isSunriseV2Enabled
      ? featureGroups!
      : this.isBusinessProduct
        ? businessFeatureGroups
        : websiteFeatureGroups;

    const missingFeatures = features.filter(this.isMonthlyDisabledInProduct);
    const missingSectionFeatures = this.missingMonthlyInSections(sections);
    const missingGroupsFeatures =
      experiments[Experiments.FeaturesGroupingPlansGrid] === 'true' ||
        isSunriseV2Enabled
        ? this.missingMonthlyInSections(groups)
        : [];

    const missingFreeCouponsFeatures = this.missingFreeCouponsFeatures();
    const missingWixelLightCouponFeatures = this.missingWixelLightCouponFeatures();

    return [
      ...missingWixelLightCouponFeatures,
      ...missingFeatures,
      ...missingFreeCouponsFeatures,
      ...missingGroupsFeatures,
      ...missingSectionFeatures,
    ];
  }

  @computed
  get disclaimerKey(): string {
    const hasEmailVoucher = (product) =>
      this.getEmailYearlyAdvancedPlanFeature(product);

    const { currentProduct } = this.props.rootStore.appStore;
    const hasQuaeterly = currentProduct.cycles.find(
      (cycle) => cycle.cycleId === PaymentCycle.QUARTERLY,
    );
    return hasQuaeterly
      ? 'cycle_entry.quarterly_disclaimer'
      : hasEmailVoucher(currentProduct)
        ? 'cycle_entry.monthly_disclaimer.with_mailbox_voucher'
        : 'cycle_entry.monthly_disclaimer';
  }

  private getEmailYearlyAdvancedPlanFeature(currentProduct) {
    const { features } = this.props.rootStore.appStore.appContext;
    return features
      .filter((f) => {
        return f.includedInProducts.has(`${currentProduct.id}`);
      })
      .find((f) => {
        return f.translationKey === Features.free_business_email_1_year.code;
      });
  }

  getCycles(product: ProductData | AdvancedPlansProductData) {
    const { setCurrentCycle } = this.props.rootStore.appStore;

    return product.cycles.map(
      (
        {
          cycleId,
          fullTermActualPrice,
          fullTermDisplayPrice,
          fullTermDisplaySavings,
          monthlyActualPrice,
          isCycleDisabledInProduct,
          monthlyFullPrice,
          firstPeriodPriceMonthly,
          firstPeriodPrice,
          fullTermPriceBeforeTax,
        },
        index,
      ) => {
        return {
          cycleDetails: {
            cycleId,
            fullTermActualPrice,
            fullTermDisplayPrice,
            fullTermDisplaySavings,
            monthlyActualPrice,
            isCycleDisabledInProduct,
            monthlyFullPrice,
            firstPeriodPriceMonthly,
            firstPeriodPrice,
            fullTermPriceBeforeTax,
            isAscendBundle: this.isAscendBundle({
              currentProduct: product,
              currentCycle: cycleId,
            }),
          },
          cellProps: {
            disabled: isCycleDisabledInProduct,
            onClick: () => setCurrentCycle({ cycle: cycleId }),
            bordered: this.isBordred(index),
            dataHook: `cycle-${cycleId}`,
          },
        };
      },
    );
  }

  @computed
  get cycles() {
    const { currentProduct } = this.props.rootStore.appStore;
    return this.getCycles(currentProduct);
  }
  @computed
  get maxPossibleCycle() {
    return this.cycles[0]?.cycleDetails?.cycleId || PaymentCycle.MONTHLY;
  }

  static getCycleNameKey(cycleId: string, showInMonths: boolean = false) {
    let key = `cycle_entry.${cycleId}.name`;
    if (showInMonths) {
      switch (cycleId) {
        case PaymentCycle.YEARLY:
          key = 'cycle_entry.12_months.name';
          break;
        case PaymentCycle.YEAR2:
          key = 'cycle_entry.24_months.name';
          break;
        case PaymentCycle.YEAR3:
          key = 'cycle_entry.36_months.name';
          break;
      }
    }
    return key;
  }

  getCurrentPlanProduct = (): AdvancedPlansProductData | null => {
    if (!this.props.rootStore.appStore.appContext.currentPlanDetails?.guid) {
      return null;
    }

    const planDetails =
      this.props.rootStore.appStore.appContext.currentPlanDetails;
    return (
      this.props.rootStore.appStore.appContext.advancedPlansProducts.find(
        (p) => p.guid === planDetails.guid,
      ) ?? null
    );
  };

  getCurrentOrDefaultProduct = () => {
    const currentProduct = this.getCurrentPlanProduct();
    const defaultProduct = this.props.rootStore.appStore.getDefaultProduct();

    return currentProduct ?? defaultProduct;
  };

  isCycleDisabledInPlansPage(cycleDetails: CycleDetails) {
    const currentDetalisCycle =
      this.props.rootStore.appStore.appContext.currentPlanDetails?.paymentCycle;
    return (
      cycleDetails.isCycleDisabledInProduct &&
      cycleDetails.cycleId !== currentDetalisCycle
    );
  }

  getCycleSavingsPercentage(cycleId: PaymentCycle) {
    if (this.props.rootStore.appStore.isInSale) {
      return getSaleDiscountPercentage();
    }

    const allProducts =
      this.props.rootStore.appStore.appContext.advancedPlansProducts || [];

    const relevantCycles = allProducts
      .flatMap((product) => product.cycles)
      .filter(
        (cycle) => cycle.cycleId === cycleId && cycle.fullTermDisplaySavings,
      );

    const maxSavingsPercentage = relevantCycles.reduce((max, cycle) => {
      const {
        fullTermDisplaySavings: savings,
        fullTermActualPrice: actualPrice,
      } = cycle;
      const savingPercentage = savings / (savings + actualPrice);
      const savingsPercentage = Math.floor(savingPercentage * 100);

      return Math.max(max, savingsPercentage);
    }, 0);

    return maxSavingsPercentage > 0 ? maxSavingsPercentage : null;
  }

  private static readonly TRANSLATION_KEYS = {
    SALE_SAVINGS:
      'premium-purchase-plan.package_picker_and_cycle_selector.feature_table.sale_savings',
    CYCLE_SAVINGS:
      'premium-purchase-plan.package_picker_and_cycle_selector.cycle_savings',
    NO_SAVINGS:
      'premium-purchase-plan.package_picker_and_cycle_selector.monthly.no_savings',
  } as const;

  getCycleSavingsLabel(
    cycleDetails: CycleDetails,
    t: WithTranslation['t'],
  ): string {
    const savingsPercentage = this.getCycleSavingsPercentage(
      cycleDetails.cycleId,
    );
    const hasSavings = Boolean(cycleDetails.fullTermDisplaySavings);
    const isInSale = this.props.rootStore.appStore.isInSale;
    const percentageText = `${savingsPercentage}%`;

    if (hasSavings && isInSale) {
      return t(CyclesController.TRANSLATION_KEYS.SALE_SAVINGS, {
        saleAmount: percentageText,
      });
    }

    if (hasSavings && !isInSale) {
      return t(CyclesController.TRANSLATION_KEYS.CYCLE_SAVINGS, {
        cycleSavings: percentageText,
      });
    }

    if (!hasSavings && isInSale) {
      return t(CyclesController.TRANSLATION_KEYS.NO_SAVINGS);
    }

    return '';
  }
}
